import type { ReactNode } from 'react';
import React from 'react';

import type { AlertProps } from '../../components/Alert/Alert';
import { backgrounds } from '../../common/backgrounds';
import { fade, palette } from '../../common/colors';
import { sizing } from '../../common/sizing';
import { Alert } from '../../components/Alert/Alert';
import { Button } from '../../controls/Button/Button';
import { colors, darkThemeSelector, styled } from '../../stitches.config';

const visualizerZIndices = {
  actions: 4,
  metadata: 3,
  display: 1,
  properties: 2,
};

const visualizerPropertiesWidth = {
  expanded: {
    desktop: 180,
    notDesktop: 200,
  },
  collapsed: 40,
};

const visualizerPadding = {
  y: 84,
  x: 48,
};

const VisualizerActions = styled('div', {
  position: 'absolute',
  top: '0.5px',
  left: '0.5px',
  zIndex: visualizerZIndices.actions,
  padding: '$8',
  background: fade(palette.bgNeutralLight, 0.5),
  borderBottomRightRadius: '$6',

  [darkThemeSelector]: {
    background: fade(palette.bgNeutralDark, 0.5),
  },
});
VisualizerActions.displayName = 'VisualizerActions';

const VisualizerConnections = styled('div', {
  position: 'absolute',
  bottom: '0.5px',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: visualizerZIndices.actions,
  padding: '$8',
  background: fade(palette.bgNeutralLight, 0.5),
  borderRadiusTop: '$6',

  [darkThemeSelector]: {
    background: fade(palette.bgNeutralDark, 0.5),
  },
});
VisualizerConnections.displayName = 'VisualizerConnections';

const VisualizerDisplay = styled('div', {
  margin: '0 auto',
  zIndex: visualizerZIndices.display,
});
VisualizerDisplay.displayName = 'VisualizerDisplay';

const VisualizerScroll = styled('div', {
  display: 'flex',
  flex: 1,
  padding: `$${visualizerPadding.y} $${visualizerPadding.x}`,
  overflow: 'auto',
  minHeight: 0,

  variants: {
    show: {
      true: {
        '@desktop': {
          padding: `$${visualizerPadding.y} $${visualizerPropertiesWidth.expanded.desktop + visualizerPadding.x} $${visualizerPadding.y} $${visualizerPadding.x}`,
        },

        '@notDesktop': {
          padding: `$${visualizerPadding.y} $${visualizerPropertiesWidth.expanded.notDesktop + visualizerPadding.x} $${visualizerPadding.y} $${visualizerPadding.x}`,
        },
      },
      false: {
        padding: `$${visualizerPadding.y} $${visualizerPropertiesWidth.collapsed + visualizerPadding.x} $${visualizerPadding.y} $${visualizerPadding.x}`,
      },
    },
  },
});
VisualizerScroll.displayName = 'VisualizerScroll';

const VisualizerPropertiesToggle = styled(Button, {
  position: 'absolute',
  top: '$8',
  left: '-$8',
  transform: 'translateX(-100%)',
  zIndex: visualizerZIndices.properties,
});
VisualizerPropertiesToggle.displayName = 'VisualizerPropertiesToggle';

const VisualizerPropertiesInner = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  padding: '$8',
  overflow: 'auto',
  gap: '$8',
});
VisualizerPropertiesInner.displayName = 'VisualizerPropertiesInner';

const VisualizerPropertiesContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '100%',
  minHeight: 0,
  overflow: 'hidden',
  background: colors.bgNeutralLight,
  strokeAll: colors.strokeNeutralLight,
  transition: 'width 0.3s ease-in-out',

  [darkThemeSelector]: {
    background: colors.bgNeutralDark,
    strokeAll: colors.strokeNeutralDark,
  },

  variants: {
    show: {
      true: {
        '@notDesktop': {
          width: `$${visualizerPropertiesWidth.expanded.notDesktop}`,
        },

        '@desktop': {
          width: `$${visualizerPropertiesWidth.expanded.desktop}`,
        },
      },
      false: {
        width: `$${visualizerPropertiesWidth.collapsed}`,
      },
    },
  },
});
VisualizerPropertiesContent.displayName = 'VisualizerPropertiesContent';

const VisualizerPropertiesApplied = styled('div', {
  position: 'absolute',
  bottom: '0.5px',
  left: '-0.5px',
  transform: 'translateX(-100%)',
  zIndex: visualizerZIndices.properties,
  padding: '$8',
  background: fade(palette.bgNeutralLight, 0.5),
  borderTopLeftRadius: '$6',

  [darkThemeSelector]: {
    background: fade(palette.bgNeutralDark, 0.5),
  },
});
VisualizerPropertiesApplied.displayName = 'VisualizerPropertiesApplied';

const VisualizerProperties = styled('div', {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  zIndex: visualizerZIndices.properties,
  display: 'flex',
});
VisualizerProperties.displayName = 'VisualizerProperties';

const VisualizerMetadata = styled('div', {
  position: 'absolute',
  bottom: '0.5px',
  left: '0.5px',
  zIndex: visualizerZIndices.metadata,
  padding: '$8',
  background: fade(palette.bgNeutralLight, 0.5),
  borderTopRightRadius: '$6',

  [darkThemeSelector]: {
    background: fade(palette.bgNeutralDark, 0.5),
  },
});
VisualizerMetadata.displayName = 'VisualizerMetadata';

const VisualizerArea = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  heightAll: 'min-content',
  overflow: 'hidden',
  background: backgrounds.dottedLight,
  backgroundColor: colors.bgNeutralLight,
  strokeAll: colors.strokeNeutralLight,

  [darkThemeSelector]: {
    background: backgrounds.dottedDark,
    backgroundColor: colors.bgNeutralDark,
    strokeAll: colors.strokeNeutralDark,
  },

  '&:first-child': {
    borderRadiusTop: '$10',
  },

  '&:last-child': {
    borderRadiusBottom: '$10',
  },
});
VisualizerArea.displayName = 'VisualizerArea';

const VisualizerAlert = styled(Alert, {
  borderRadius: '0 !important',

  '&:first-child': {
    borderRadiusTop: '$10 !important',
  },

  '&:last-child': {
    borderRadiusBottom: '$10 !important',
  },
});
VisualizerAlert.displayName = 'VisualizerAlert';

const VisualizerContainer = styled('div', {
  width: '100%',
  heightAll: 'min-content',
  padding: sizing.squish,
});
VisualizerContainer.displayName = 'VisualizerContainer';

export type VisualizerProps = {
  /**
   * The actions to display on the visualizer.
   */
  actions?: ReactNode;
  /**
   * The alert to display on the visualizer.
   */
  alert?: AlertProps;
  /**
   * The connections to display on the visualizer.
   */
  connections?: ReactNode;
  /**
   * The core of the visualizer where the diagrams/flows appear.
   */
  display: ReactNode;
  /**
   * The properties of what is being displayed such as legends and glossaries.
   */
  properties?: {
    show?: boolean;
    setShow?: (show: boolean) => void;
    content: ReactNode;
    applied?: ReactNode;
  };
  /**
   * Used to display metadata about the visualizer such as the last updated time.
   */
  metadata?: ReactNode;
};

export function Visualizer({
  actions,
  alert,
  connections,
  display,
  metadata,
  properties,
  ...remaining
}: VisualizerProps) {
  return (
    <VisualizerContainer {...remaining}>
      {alert && <VisualizerAlert {...alert} />}
      <VisualizerArea>
        {actions && <VisualizerActions>{actions}</VisualizerActions>}
        {metadata && <VisualizerMetadata>{metadata}</VisualizerMetadata>}
        {connections && <VisualizerConnections>{connections}</VisualizerConnections>}
        <VisualizerScroll show={properties?.show}>
          <VisualizerDisplay>{display}</VisualizerDisplay>
        </VisualizerScroll>
        {properties && properties.content && (
          <VisualizerProperties>
            <VisualizerPropertiesToggle
              arrangement="hidden-label"
              icon={properties.show ? 'pane-right' : 'pane-left'}
              ghost
              size="small"
              variant="secondary"
              onClick={() => properties.setShow?.(!properties.show)}
            >
              {properties.show ? 'Hide' : 'Show'} properties
            </VisualizerPropertiesToggle>
            <VisualizerPropertiesContent show={properties.show}>
              <VisualizerPropertiesInner>{properties.content}</VisualizerPropertiesInner>
            </VisualizerPropertiesContent>
            {properties.applied && (
              <VisualizerPropertiesApplied>{properties.applied}</VisualizerPropertiesApplied>
            )}
          </VisualizerProperties>
        )}
      </VisualizerArea>
    </VisualizerContainer>
  );
}
